import type { ActiveElement, ChartEvent } from "chart.js";
import { ArcElement, Chart, Legend } from "chart.js";
import type { ComponentProps } from "react";
import { useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";

import { Sources } from "~/components/charts/sources";
import { abbreviatedNum } from "~/lib/abbreviated-num";
import {
  CHART_BACKGROUND_COLORS,
  CHART_BACKGROUND_HOVER_COLORS,
  CHART_BORDER_COLORS,
  CHART_BORDER_HOVER_COLORS,
} from "~/lib/colors";

Chart.register([ArcElement, Legend]);

const MIN_PERCENTAGE = 0.005;

const PERCENT = 0.01;

type Props = Readonly<{
  colors?: Readonly<{
    backgroundColor: readonly string[];
    borderColor: readonly string[];
    hoverBackgroundColor: readonly string[];
    hoverBorderColor: readonly string[];
  }>;
  labels: string[];
  options?: ComponentProps<typeof Doughnut>["options"];
  sources?: readonly string[];
  values: readonly number[];
}>;

type GetSummariesReq = Readonly<{
  labels: string[];
  values: readonly number[];
  minPercentage: number;
}>;

type GetSummariesRes = Readonly<{
  label: string;
  value: number;
  truePercentage: number;
  viewPercentage: number;
}>;

export function Donut({
  colors = {
    backgroundColor: CHART_BACKGROUND_COLORS,
    borderColor: CHART_BORDER_COLORS,
    hoverBackgroundColor: CHART_BACKGROUND_HOVER_COLORS,
    hoverBorderColor: CHART_BORDER_HOVER_COLORS,
  },
  labels,
  options,
  sources = [],
  values,
}: Props): JSX.Element {
  const summaries = useMemo(
    () => getSummaries({ labels, values, minPercentage: MIN_PERCENTAGE }),
    [labels, values]
  );
  const [entryIdx, setEntryIdx] = useState(0);

  function handleHover(_event: ChartEvent, elements: ActiveElement[]) {
    if (elements[0]) {
      setEntryIdx(elements[0].index);
    }
  }

  function formatValue() {
    return `$${abbreviatedNum(summaries[entryIdx].value)}`;
  }

  function formatPercentage() {
    const decimals = summaries[entryIdx].truePercentage < PERCENT ? 3 : 2;
    return `${(100 * summaries[entryIdx].truePercentage).toFixed(decimals)}%`;
  }

  function formatLabel() {
    return summaries[entryIdx].label;
  }

  return (
    <>
      <div className="relative my-6 md:my-0 h-[500px]">
        <div className="flex flex-col items-center w-full h-full absolute top-0 justify-center">
          <span>{formatLabel()}</span>
          <div className="flex items-center gap-x-2 md:gap-x-4 md:text-xl">
            <span>{formatValue()}</span>
            <div className="rounded-full h-[6px] w-[6px] bg-gray-500 dark:bg-gray-400" />
            <span>{formatPercentage()}</span>
          </div>
        </div>
        <div className="relative h-full w-full">
          <Doughnut
            data={{
              labels,
              datasets: [
                {
                  ...colors,
                  borderWidth: 1,
                  data: summaries.map((entry) => entry.viewPercentage),
                },
              ],
            }}
            options={{
              cutout: "75%",
              elements: { point: { radius: 0 } },
              interaction: { mode: "nearest", intersect: true },
              maintainAspectRatio: false,
              onHover: handleHover,
              plugins: {
                legend: { labels: { boxHeight: 5, usePointStyle: true } },
                tooltip: { enabled: false },
              },
              responsive: true,
              ...options,
            }}
          />
        </div>
      </div>
      <Sources sources={sources} />
    </>
  );
}

function getSummaries({
  labels,
  values,
  minPercentage,
}: GetSummariesReq): GetSummariesRes[] {
  const total = values.reduceRight((acc, v) => acc + v);
  let adjustedValueSum = 0;
  const entries = labels.map((l, i) => {
    const truePercentage = values[i] / total;
    adjustedValueSum += truePercentage < minPercentage ? values[i] : 0;
    return {
      label: l,
      value: values[i],
      truePercentage,
      viewPercentage: Math.max(truePercentage, minPercentage),
    };
  });

  function adjustValuesProportionally() {
    const adjustedTotal = total - adjustedValueSum;
    return entries.map((e, i) => ({
      ...e,
      viewPercentage:
        entries[i].truePercentage > minPercentage
          ? values[i] / adjustedTotal
          : minPercentage,
    }));
  }

  return adjustValuesProportionally();
}
